import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardCheck, Target, FolderOpen } from "lucide-react";

export const CHECKLIST_ITEMS = [
  { id: "convention", label: "Ma convention de stage est signée (lycée, entreprise, famille)" },
  { id: "tuteur", label: "Je connais mon tuteur, l'adresse et les horaires de l'entreprise" },
  { id: "trajet", label: "J'ai repéré mon trajet et mon heure de départ" },
  { id: "tenue", label: "J'ai préparé une tenue professionnelle adaptée" },
  { id: "objectifs", label: "J'ai relu mes objectifs de PFMP (ci-dessous)" },
  { id: "fiches", label: "J'ai mes fiches portfolio à compléter pendant le stage" },
  { id: "livret", label: "J'ai mon livret PFMP dans mon sac" },
];

export default function PfmpChecklistDialog({ pfmp, open, onOpenChange }) {
  const [checked, setChecked] = useState([]);

  useEffect(() => {
    if (!open || !pfmp?.id) return;
    api.get(`/pfmp/checklist/${pfmp.id}`).then(({ data }) => setChecked(data.checked || []));
  }, [open, pfmp?.id]);

  const toggle = (id) => {
    const next = checked.includes(id) ? checked.filter((c) => c !== id) : [...checked, id];
    setChecked(next);
    api.put(`/pfmp/checklist/${pfmp.id}`, { checked: next }).catch(() => {});
  };

  const done = CHECKLIST_ITEMS.filter((i) => checked.includes(i.id)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pfmp-checklist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-sky-600" /> Préparer ma {pfmp?.title}
          </DialogTitle>
          <DialogDescription className="font-medium">{pfmp?.period}</DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl bg-sky-50 px-4 py-3">
          <div className="flex items-center justify-between text-sm font-bold text-sky-700">
            <span>Ma préparation</span>
            <span data-testid="pfmp-checklist-progress">{done} / {CHECKLIST_ITEMS.length}</span>
          </div>
          <div className="h-2 rounded-full bg-sky-100 mt-2 overflow-hidden">
            <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${(done / CHECKLIST_ITEMS.length) * 100}%` }} />
          </div>
        </div>

        <div className="space-y-2">
          {CHECKLIST_ITEMS.map((item) => (
            <label key={item.id} data-testid={`pfmp-check-${item.id}`}
              className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-white hover:bg-slate-50 transition-colors p-3.5 cursor-pointer">
              <Checkbox checked={checked.includes(item.id)} onCheckedChange={() => toggle(item.id)} className="mt-0.5" />
              <span className={"text-sm font-medium " + (checked.includes(item.id) ? "text-slate-400 line-through" : "text-slate-700")}>
                {item.label}
              </span>
            </label>
          ))}
        </div>

        {pfmp?.objective && (
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 flex items-center gap-1.5 mb-1">
              <Target className="h-3.5 w-3.5" /> Mes objectifs de PFMP
            </p>
            <p className="text-sm text-slate-600 font-medium leading-relaxed">{pfmp.objective}</p>
          </div>
        )}
        {pfmp?.fiches && (
          <div className="rounded-2xl bg-orange-50 p-4">
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-orange-500 flex items-center gap-1.5 mb-1">
              <FolderOpen className="h-3.5 w-3.5" /> À rapporter au lycée
            </p>
            <p className="text-sm text-orange-700 font-medium leading-relaxed">{pfmp.fiches}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
